import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, MessageSquare, Building2, User, CheckCircle2, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import WaitlistModal from "@/components/WaitlistModal";

/* Same character allow-lists used on Signup (P3 #12), so the Contact form
   has the same input behaviour. */
const NAME_REGEX = /^[A-Za-z ]*$/;
const ORG_REGEX = /^[A-Za-z\-&'. ]*$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MAX_MESSAGE = 2000;
const MIN_MESSAGE = 10;

const SUBJECTS = [
  "General enquiry",
  "Sales / pricing",
  "Product demo",
  "Partnership",
  "Press / media",
  "Support",
  "Other",
];

export default function ContactUs() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organisation: "",
    subject: SUBJECTS[0],
    message: "",
    website: "", // honeypot — must stay empty for real users
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useState("");
  const [showWaitlist, setShowWaitlist] = useState(false);

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
    setServerError("");
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2) e.name = "Please enter your full name.";
    else if (!NAME_REGEX.test(form.name)) e.name = "Use letters and spaces only.";

    if (!form.email.trim()) e.email = "Email is required.";
    else if (!EMAIL_REGEX.test(form.email.trim())) e.email = "Enter a valid email address.";

    if (form.organisation && !ORG_REGEX.test(form.organisation)) {
      e.organisation = "Letters, spaces, '-', '&', \".\", apostrophes only.";
    }

    if (!form.subject) e.subject = "Pick a subject.";

    const msg = form.message.trim();
    if (msg.length < MIN_MESSAGE) e.message = `Add at least ${MIN_MESSAGE} characters so we can help.`;
    else if (msg.length > MAX_MESSAGE) e.message = `Keep it under ${MAX_MESSAGE} characters.`;

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    setServerError("");
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        organisation: form.organisation.trim() || null,
        subject: form.subject,
        message: form.message.trim(),
        website: form.website || null,
      });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setServerError(
        typeof detail === "string"
          ? detail
          : "Something went wrong sending your message. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  const remaining = MAX_MESSAGE - form.message.length;

  return (
    <div className="min-h-screen flex flex-col bg-white text-[#0F1F3D] lr-radial relative overflow-hidden ">
      {/* Header (matches Landing) */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src='/logo.png' alt='LaunchRadar' className='mx-auto w-[200px]' />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#475569]">
            <a href="/" className="hover:text-[#0F1F3D]">Use cases</a>
            <a href="/" className="hover:text-[#0F1F3D]">How it works</a>
            <a href="/" className="hover:text-[#0F1F3D]">Why us</a>
            <Link to="/contact" className="text-[#0F1F3D] font-medium">Contact</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="header-login-link" className="text-sm text-[#475569] hover:text-[#0F1F3D]">Sign in</Link>
            <button type="button" data-testid="header-signup-cta cursor-pointer" className="px-4 py-2 rounded-md bg-[#0F1F3D] text-white text-sm font-medium hover:bg-[#172A4F]" onClick={() => setShowWaitlist(true)}>
              Join Waitlist
            </button>
          </div>
        </div>
      </header>

      <section className="flex-1 max-w-7xl w-full mx-auto px-6 pt-8 pb-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        {/* Left column — pitch */}
        <div className="lg:col-span-5">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#E2E8F0] bg-white type-caption text-[#475569] mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" />
            We reply within one business day
          </div>
          <h1 className="font-display font-bold leading-[1.05] tracking-tight text-3xl sm:text-4xl lg:text-5xl">
            Talk to the<br />
            <span className="text-[#10B981]">LaunchRadar</span> team.
          </h1>
          <p className="mt-4 type-body text-[#475569] prose-measure">
            Whether you want a live walkthrough, custom sources for your project,
            or pricing for a larger team — drop us a note and a human will get back to you.
          </p>

          <div className="mt-5 space-y-3">
            <div className="flex items-start gap-3 p-3 rounded-lg border border-[#E2E8F0] bg-white">
              <div className="h-9 w-9 rounded-md bg-[#0F1F3D]/8 flex items-center justify-center shrink-0">
                <Mail className="h-4 w-4 text-[#0F1F3D]" />
              </div>
              <div>
                <div className="text-sm font-semibold text-[#0F1F3D]">Email us directly</div>
                <a
                  href="mailto:info@launchradar.live"
                  className="text-xs text-[#0F1F3D] underline underline-offset-2 hover:text-[#10B981]"
                  data-testid="contact-email-link"
                >
                  info@launchradar.live
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg border border-[#E2E8F0] bg-white">
              <div className="h-9 w-9 rounded-md bg-[#0F1F3D]/8 flex items-center justify-center shrink-0">
                <Building2 className="h-4 w-4 text-[#0F1F3D]" />
              </div>
              <div>
                <div className="text-sm font-semibold text-[#0F1F3D]">For teams &amp; enterprises</div>
                <div className="text-xs text-[#475569]">
                  Custom seats, SSO, and dedicated source onboarding available on the Growth plan and above.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right column — form */}
        <div className="lg:col-span-7">
          <div className="relative">
            <div className="absolute -inset-3 bg-gradient-to-br from-[#0F1F3D]/6 to-[#10B981]/10 rounded-3xl blur-2xl" />
            <div className="relative bg-white border border-[#E2E8F0] rounded-2xl shadow-[0_30px_80px_-20px_rgba(15,31,61,0.18)] p-5 sm:p-6">
              {sent ? (
                <div className="text-center py-8" data-testid="contact-success-state">
                  <div className="h-12 w-12 rounded-full bg-[#ECFDF5] mx-auto flex items-center justify-center mb-3">
                    <CheckCircle2 className="h-6 w-6 text-[#10B981]" />
                  </div>
                  <h2 className="font-display text-xl font-bold text-[#0F1F3D]">Message sent</h2>
                  <p className="text-sm text-[#475569] mt-2 max-w-md mx-auto">
                    Thanks for reaching out. We&apos;ll reply to{" "}
                    <span className="text-[#0F1F3D] font-semibold">{form.email}</span>{" "}
                    within one business day.
                  </p>
                  <button
                    onClick={() => {
                      setForm({
                        name: "", email: "", organisation: "",
                        subject: SUBJECTS[0], message: "", website: "",
                      });
                      setSent(false);
                    }}
                    data-testid="contact-send-another-btn"
                    className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-md border border-[#0F1F3D] text-[#0F1F3D] text-sm font-medium hover:bg-[#F8F9FA]"
                  >
                    Send another message <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate data-testid="contact-form" className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-md bg-[#10B981]/15 flex items-center justify-center">
                      <MessageSquare className="h-4 w-4 text-[#047857]" />
                    </div>
                    <h2 className="font-display text-xl font-bold text-[#0F1F3D]">Send us a message</h2>
                  </div>

                  {/* Honeypot — hidden from real users */}
                  <div
                    aria-hidden="true"
                    style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}
                  >
                    <label>
                      Website
                      <input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={form.website}
                        onChange={(e) => update("website", e.target.value)}
                      />
                    </label>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Your name</label>
                      <div className="relative mt-1">
                        <User className="h-4 w-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          value={form.name}
                          onChange={(e) => NAME_REGEX.test(e.target.value) && update("name", e.target.value)}
                          placeholder="Jane Doe"
                          data-testid="contact-name-input"
                          className={`w-full pl-9 pr-3 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-[#0F1F3D] text-sm ${errors.name ? "border-[#FCA5A5]" : "border-[#E2E8F0]"}`}
                        />
                      </div>
                      {errors.name && <p className="mt-1 text-xs text-[#B91C1C]">{errors.name}</p>}
                    </div>

                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Work email</label>
                      <div className="relative mt-1">
                        <Mail className="h-4 w-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          type="email"
                          value={form.email}
                          onChange={(e) => update("email", e.target.value)}
                          placeholder="jane@company.com"
                          data-testid="contact-email-input"
                          className={`w-full pl-9 pr-3 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-[#0F1F3D] text-sm ${errors.email ? "border-[#FCA5A5]" : "border-[#E2E8F0]"}`}
                        />
                      </div>
                      {errors.email && <p className="mt-1 text-xs text-[#B91C1C]">{errors.email}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Organisation <span className="text-[#94A3B8] normal-case tracking-normal font-normal">(optional)</span></label>
                      <div className="relative mt-1">
                        <Building2 className="h-4 w-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          value={form.organisation}
                          onChange={(e) => ORG_REGEX.test(e.target.value) && update("organisation", e.target.value)}
                          placeholder="Acme Industries"
                          data-testid="contact-org-input"
                          className={`w-full pl-9 pr-3 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-[#0F1F3D] text-sm ${errors.organisation ? "border-[#FCA5A5]" : "border-[#E2E8F0]"}`}
                        />
                      </div>
                      {errors.organisation && <p className="mt-1 text-xs text-[#B91C1C]">{errors.organisation}</p>}
                    </div>

                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Subject</label>
                      <select
                        value={form.subject}
                        onChange={(e) => update("subject", e.target.value)}
                        data-testid="contact-subject-select"
                        className="mt-1 w-full px-3 py-2 rounded-md border border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#0F1F3D] text-sm bg-white"
                      >
                        {SUBJECTS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Message</label>
                    <textarea
                      value={form.message}
                      onChange={(e) => update("message", e.target.value.slice(0, MAX_MESSAGE))}
                      placeholder="Tell us a little about what you're trying to track and how we can help…"
                      rows={3}
                      maxLength={MAX_MESSAGE}
                      data-testid="contact-message-input"
                      className={`mt-1 w-full px-3 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-[#0F1F3D] text-sm resize-none ${errors.message ? "border-[#FCA5A5]" : "border-[#E2E8F0]"}`}
                    />
                    <div className="flex items-center justify-between mt-1">
                      {errors.message
                        ? <p className="text-xs text-[#B91C1C]">{errors.message}</p>
                        : <span className="text-xs text-[#94A3B8]">We read every message — please be specific.</span>}
                      <span className={`text-xs font-mono-tab ${remaining < 100 ? "text-[#B91C1C]" : "text-[#94A3B8]"}`} data-testid="contact-char-counter">
                        {remaining}
                      </span>
                    </div>
                  </div>

                  {serverError && (
                    <div className="text-sm text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-md px-3 py-2" data-testid="contact-server-error">
                      {serverError}
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <button
                      type="submit"
                      disabled={busy}
                      data-testid="contact-submit-btn"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#0F1F3D] text-white font-medium hover:bg-[#172A4F] disabled:opacity-60 text-sm"
                    >
                      {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Send message <ArrowRight className="h-4 w-4" /></>}
                    </button>
                    <p className="text-[11px] text-[#94A3B8] flex-1 min-w-[180px]">
                      By submitting this form you agree to be contacted by LaunchRadar regarding your enquiry.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E2E8F0] py-10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between text-xs text-[#64748B]">
          <div>© 2026 LaunchRadar</div>
          <div className="flex items-center gap-6">
            <Link
              to="/privacy-policy"
              className="hover:text-[#0F1F3D]"
            >
              Privacy Policy
            </Link>

            <Link
              to="/terms-and-conditions"
              className="hover:text-[#0F1F3D]"
            >
              Terms & Conditions
            </Link>

            <Link
              to="/faq"
              className="hover:text-[#0F1F3D]"
            >
              FAQs
            </Link>

            <Link
              to="/contact"
              className="font-medium text-[#0F1F3D]"
            >
              Contact
            </Link>
          </div>
        </div>
      </footer>

      {showWaitlist && (
        <WaitlistModal
          onClose={() => setShowWaitlist(false)}
        />
      )}
    </div>
  );
}
